import React, { useCallback } from 'react';
import { createStyles, Theme, Dialog } from "@material-ui/core";
import { Form, Formik } from "formik";
import * as Yup from 'yup';
import { ReactSVG } from "react-svg";
import { Button } from "../Button";
import { strings } from "../../strings/strings";
import { addYears } from "../../utils/dateUtils";
import { InputText } from "../inputs/InputText";
import { FormItem } from "../inputs/FormItem";
import { DatePicker } from "../inputs/DatePicker";
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useReminderPopup } from './hook/useReminderPopup';
import { sendEvent } from "../../api/reminderApiClient";
import popupTitleImage from "../../images/widget-title.svg";

import "./index.scss"

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        backdrop: {
            zIndex: theme.zIndex.drawer + 1,
        },
    })
);

const validationSchema = Yup.object().shape({
    email: Yup.string()
        .required(strings.mtlpReminderDialog.validation.required)
        .email(strings.mtlpReminderDialog.validation.format),
    date: Yup.date()
        .nullable()
        .required(strings.mtlpReminderDialog.validation.required)
        .min(new Date(), strings.mtlpReminderDialog.validation.pastDateError)
        .typeError(strings.mtlpReminderDialog.validation.format)
});

export enum EventTypeKey {
    AVTOCIV = 'avtocivwebshop',
    TRAVEL = 'turwebshop',
    GREEN_CARD = 'zkwebshop'
}
interface ReminderPopupProps {
    eventTypeKey: EventTypeKey;
}

export const ReminderPopup: React.FC<ReminderPopupProps> = ({ eventTypeKey }) => {
    const classes = useStyles();
    const { isPopupVisible, setIsPopupVisible, closePopup } = useReminderPopup();

    const handleBackdropClick = useCallback((e) => {
        e.stopPropagation();
    }, []);

    const onSubmit = async (values: { email: string; date: string }, { setSubmitting }) => {
        const keyValue = values.email;
        const params = [
            { name: "email", value: values.email },
            { name: "date", value: values.date },
            { name: "url", value: window.location.href }
        ];
        sendEvent({
            eventTypeKey,
            keyValue,
            params
        })
            .catch((e) => console.error("Error during form submission", e))
            .finally(() => {
                setIsPopupVisible(false);
                setSubmitting(false);
            })
    };

    return (
        <Dialog open={isPopupVisible} onClick={closePopup} className={classes.backdrop}>
            <div onClick={handleBackdropClick} className={"dialog-one reminder-popup"}>
                <div className={'dialog-one__close'} onClick={closePopup}></div>
                <ReactSVG src={popupTitleImage} className={"dialog-one__title_image"} />
                <div className={"dialog-one__title"}>{strings.mtlpReminderDialog.title}</div>
                <div className={"dialog-one__sub_title"}>{strings.mtlpReminderDialog.subTitle}</div>
                <Formik
                    initialValues={{
                        email: '',
                        date: null,
                    }}
                    onSubmit={onSubmit}
                    validationSchema={validationSchema}
                    validateOnBlur={false}
                    validateOnChange={false}
                >
                    {formProps => (
                        <Form>
                            <div className="dialog-one__content">
                                <FormItem name={'date'} validation={{ isRequired: true }}>
                                    <DatePicker
                                        label={strings.mtlpReminderDialog.datePlaceholder}
                                        minDate={addYears(new Date(), 0)}
                                        maxDate={addYears(new Date(), 100)}
                                        name="date"
                                        value={formProps.values.date}
                                        onChange={(date) => formProps.setFieldValue('date', date)}
                                        onBlur={formProps.handleBlur}
                                    />
                                </FormItem>
                                <FormItem name={'email'} validation={{ isRequired: true }}>
                                    <InputText
                                        label={strings.mtlpReminderDialog.emailPlaceholder}
                                        type="email"
                                        name="email"
                                        value={formProps.values.email}
                                        onChange={formProps.handleChange}
                                        onBlur={formProps.handleBlur}
                                    />
                                </FormItem>
                                <Button
                                    id={'toolbarCheck'}
                                    label={strings.mtlpReminderDialog.subscribe}
                                    isProcess={formProps.isSubmitting}
                                    onClickListener={formProps.handleSubmit}
                                />
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </Dialog>
    );
};